import React, { useEffect, useRef, useState } from 'react';
import { useDescText } from 'src/hooks/useDescText';
import { DESC_BTN } from 'src/utils/descLanguage/descLanguageUtil';

interface ImagePreviewModalProps {
	isOpen: boolean;
	title: string;
	image: string;
	onClose: () => void;
}

interface ImagePosition {
	x: number;
	y: number;
}

const MIN_IMAGE_SCALE = 0.25;
const MAX_IMAGE_SCALE = 4;
const IMAGE_SCALE_STEP = 0.25;
const IMAGE_MOVE_STEP = 40;

export const ImagePreviewModal = ({ isOpen, title, image, onClose }: ImagePreviewModalProps) => {
	const { renderDescText } = useDescText();
	const viewportRef = useRef<HTMLDivElement>(null);
	const imageRef = useRef<HTMLImageElement>(null);
	const dragStartRef = useRef({ pointerId: -1, clientX: 0, clientY: 0, positionX: 0, positionY: 0 });
	const [scale, setScale] = useState(1);
	const [position, setPosition] = useState<ImagePosition>({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const [imageError, setImageError] = useState(false);

	const getClampedPosition = (nextPosition: ImagePosition, nextScale: number): ImagePosition => {
		const viewport = viewportRef.current;
		const previewImage = imageRef.current;
		if (!viewport || !previewImage || nextScale <= 1) return { x: 0, y: 0 };

		const maxX = Math.max(0, (previewImage.offsetWidth * nextScale - viewport.clientWidth) / 2);
		const maxY = Math.max(0, (previewImage.offsetHeight * nextScale - viewport.clientHeight) / 2);
		return {
			x: Math.min(maxX, Math.max(-maxX, nextPosition.x)),
			y: Math.min(maxY, Math.max(-maxY, nextPosition.y)),
		};
	};

	const resetImageView = () => {
		setScale(1);
		setPosition({ x: 0, y: 0 });
		setIsDragging(false);
	};

	const handleScaleChange = (nextScale: number) => {
		const clampedScale = Math.min(MAX_IMAGE_SCALE, Math.max(MIN_IMAGE_SCALE, nextScale));
		setScale(clampedScale);
		setPosition(current => getClampedPosition(current, clampedScale));
	};

	const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
		if (scale <= 1) return;
		event.currentTarget.focus();
		event.currentTarget.setPointerCapture(event.pointerId);
		dragStartRef.current = {
			pointerId: event.pointerId,
			clientX: event.clientX,
			clientY: event.clientY,
			positionX: position.x,
			positionY: position.y,
		};
		setIsDragging(true);
	};

	const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
		if (!isDragging || dragStartRef.current.pointerId !== event.pointerId) return;
		setPosition(getClampedPosition({
			x: dragStartRef.current.positionX + event.clientX - dragStartRef.current.clientX,
			y: dragStartRef.current.positionY + event.clientY - dragStartRef.current.clientY,
		}, scale));
	};

	const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
		if (dragStartRef.current.pointerId !== event.pointerId) return;
		if (event.currentTarget.hasPointerCapture(event.pointerId)) {
			event.currentTarget.releasePointerCapture(event.pointerId);
		}
		dragStartRef.current.pointerId = -1;
		setIsDragging(false);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (scale <= 1) return;
		const moveByKey: Record<string, ImagePosition> = {
			ArrowUp: { x: 0, y: IMAGE_MOVE_STEP },
			ArrowDown: { x: 0, y: -IMAGE_MOVE_STEP },
			ArrowLeft: { x: IMAGE_MOVE_STEP, y: 0 },
			ArrowRight: { x: -IMAGE_MOVE_STEP, y: 0 },
		};
		const move = moveByKey[event.key];
		if (!move) return;
		event.preventDefault();
		setPosition(current => getClampedPosition({ x: current.x + move.x, y: current.y + move.y }, scale));
	};

	useEffect(() => {
		if (!isOpen) return;
		setScale(1);
		setPosition({ x: 0, y: 0 });
		setIsDragging(false);
		setImageError(false);
	}, [isOpen, image]);

	useEffect(() => {
		if (!isOpen) return;
		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', handleEscape);
		return () => window.removeEventListener('keydown', handleEscape);
	}, [isOpen, onClose]);

	if (!isOpen || !image) return null;

	const mimeType = image.startsWith('/9j/') ? 'image/jpeg' : 'image/png';

	return (
		<div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/45" onClick={event => {
			if (event.target === event.currentTarget) onClose();
		}}>
			<div role="dialog" aria-modal="true" aria-label={title} className="flex h-[95vh] w-[95vw] flex-col overflow-hidden rounded-[12px] bg-white shadow-2xl">
				<div className="flex h-[56px] shrink-0 items-center justify-between bg-blue-dark px-[20px] text-[20px] text-white">
					<span className="truncate font-semibold">{title}</span>
					<button type="button" className="h-[48px] w-[48px] text-[30px]" onClick={onClose} aria-label={renderDescText(DESC_BTN.CLOSE, '닫기')}>×</button>
				</div>
				<div
					ref={viewportRef}
					className={`relative flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-gray-100 outline-none ${scale > 1 ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}`}
					style={{ touchAction: scale > 1 ? 'none' : 'auto' }}
					tabIndex={0}
					onPointerDown={handlePointerDown}
					onPointerMove={handlePointerMove}
					onPointerUp={handlePointerUp}
					onPointerCancel={handlePointerUp}
					onKeyDown={handleKeyDown}
					aria-label="확대 이미지 영역"
				>
					{imageError ? (
						<p className="text-[20px] text-gray-700">이미지를 표시할 수 없습니다.</p>
					) : (
						<img
							ref={imageRef}
							className="pointer-events-none h-full w-full select-none object-contain"
							src={`data:${mimeType};base64,${image}`}
							alt={title}
							draggable={false}
							onError={() => setImageError(true)}
							style={{ transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${scale})`, transformOrigin: 'center' }}
						/>
					)}
				</div>
				<div className="flex min-h-[64px] shrink-0 flex-wrap items-center justify-center gap-[8px] border-t border-gray-300 p-[8px]">
					<button type="button" className="h-[48px] min-w-[48px] rounded bg-blue-dark px-[12px] text-[22px] text-white disabled:opacity-40" onClick={() => handleScaleChange(scale - IMAGE_SCALE_STEP)} disabled={scale <= MIN_IMAGE_SCALE} aria-label="이미지 축소">−</button>
					<span className="flex h-[48px] min-w-[76px] items-center justify-center rounded bg-blue-dark px-[12px] text-[18px] text-white">{Math.round(scale * 100)}%</span>
					<button type="button" className="h-[48px] min-w-[48px] rounded bg-blue-dark px-[12px] text-[22px] text-white disabled:opacity-40" onClick={() => handleScaleChange(scale + IMAGE_SCALE_STEP)} disabled={scale >= MAX_IMAGE_SCALE} aria-label="이미지 확대">+</button>
					<button type="button" className="h-[48px] rounded bg-blue-dark px-[18px] text-[18px] text-white" onClick={resetImageView}>맞춤</button>
					<button type="button" className="h-[48px] rounded bg-gray-500 px-[18px] text-[18px] text-white" onClick={onClose}>{renderDescText(DESC_BTN.CLOSE, '닫기')}</button>
				</div>
			</div>
		</div>
	);
};
